// ===================================================================
// src/app/lib/chatNotifySocket.ts
//
// Canal WS por-usuário (não por-grupo) do chat interno — avisa sobre
// mensagem nova em QUALQUER grupo acessível, mesmo quando o grupo não
// está aberto no momento. Protocolo: ws(s)://<api-host>/ws/chat/notify/
// ?token=<access_token> (apps/chat/consumers.py::ChatNotifyConsumer +
// apps/chat/routing.py).
//
// Instância única para o app inteiro (conectada em AppLayout.tsx) — ao
// contrário de chatSocket.ts (uma conexão nova por grupo aberto), aqui
// uma única conexão cobre todos os grupos do usuário logado. Mesmo
// estilo fábrica/closure de chatSocket.ts/whatsappChatSocket.ts, mas
// exportada já instanciada (singleton) em vez de uma factory — não faz
// sentido ter mais de uma conexão de notificação por sessão.
// ===================================================================

import { getAccessToken } from './api';

export interface ChatNotifyLastMessage {
  content: string;
  author_name: string;
  created_at: string;
  is_deleted: boolean;
}

export interface ChatNotifyPayload {
  group_id: string;
  unread_count: number;
  last_message: ChatNotifyLastMessage;
}

export type ChatNotifyEventType = 'open' | 'chat.notify.update';

type EventHandler<T = unknown> = (payload: T) => void;

const RECONNECT_DELAY_MS = 2000;
const MAX_RECONNECT_ATTEMPTS = 5;
// 4001 = não autenticado (sem token) — não faz sentido tentar de novo
// sem uma sessão válida.
const PERMANENT_FAILURE_CODES = new Set([4001]);

function buildWsUrl(token: string): string {
  const rawBase = (import.meta.env.VITE_API_URL ?? 'http://localhost:8000').replace(/\/+$/, '');
  const wsProtocol = rawBase.startsWith('https') ? 'wss' : 'ws';
  const hostAndPort = rawBase.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${hostAndPort}/ws/chat/notify/?token=${encodeURIComponent(token)}`;
}

function createChatNotifySocket() {
  let ws: WebSocket | null = null;
  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let manuallyDisconnected = true;

  const handlers = new Map<string, Set<EventHandler<any>>>();

  function emit(type: string, payload: unknown) {
    handlers.get(type)?.forEach((h) => h(payload));
  }

  function clearReconnectTimer() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function open() {
    const token = getAccessToken();
    if (!token) return;

    const socket = new WebSocket(buildWsUrl(token));

    socket.onopen = () => {
      reconnectAttempts = 0;
      emit('open', undefined);
    };

    socket.onmessage = (event) => {
      let data: { type?: string; payload?: unknown };
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!data.type) return;
      emit(data.type, data.payload);
    };

    socket.onclose = (event) => {
      ws = null;
      if (manuallyDisconnected) return;
      if (PERMANENT_FAILURE_CODES.has(event.code) || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;

      reconnectAttempts += 1;
      clearReconnectTimer();
      reconnectTimer = setTimeout(open, RECONNECT_DELAY_MS);
    };

    socket.onerror = () => {
      // onclose é sempre disparado logo em seguida — reconexão fica lá.
    };

    ws = socket;
  }

  return {
    /** Idempotente — chamar de novo com uma conexão já aberta não faz nada. */
    connect() {
      if (ws) return;
      manuallyDisconnected = false;
      reconnectAttempts = 0;
      open();
    },

    disconnect() {
      manuallyDisconnected = true;
      clearReconnectTimer();
      if (ws) {
        ws.onclose = null;
        ws.close(1000, 'client disconnect');
        ws = null;
      }
    },

    on(type: ChatNotifyEventType, handler: EventHandler<any>) {
      if (!handlers.has(type)) handlers.set(type, new Set());
      handlers.get(type)!.add(handler);
    },

    off(type: string, handler: EventHandler<any>) {
      handlers.get(type)?.delete(handler);
    },
  };
}

/** Instância única compartilhada por todo o app — conectada em AppLayout.tsx. */
export const chatNotifySocket = createChatNotifySocket();
